import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";

import { supportGetUserErrors } from "src/apiService/modules/support";
import type { ClientErrorRecord } from "src/interfaces/errorStats.interface";
import ClientErrorsTable from "src/pages/support/ClientErrorsTable";
import { setSupportLoginToken } from "src/store/support/actions";
import { supportGetToken } from "src/store/support/selector";

const useStyles = makeStyles((theme) => ({
  section: {
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
}));

interface ErrorsSectionProps {
  userId: string;
}

function ErrorsSection({ userId }: ErrorsSectionProps) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const token = useSelector(supportGetToken);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [errors, setErrors] = useState<ClientErrorRecord[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const load = useCallback(
    async (cursor?: string) => {
      if (!token || !userId) return;

      if (cursor) setLoadingMore(true);
      else setLoading(true);

      try {
        const { data } = await supportGetUserErrors(token, userId, {
          limit: 25,
          cursor,
        });
        setErrors((prev) => (cursor ? [...prev, ...data.errors] : data.errors));
        setNextCursor(data.nextCursor);
      } catch (err: any) {
        if (err?.code === "failed-precondition") {
          dispatch(setSupportLoginToken());
        }
        if (!cursor) {
          setErrors([]);
          setNextCursor(null);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [dispatch, token, userId]
  );

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Box className={classes.section}>
      <Typography variant="h6" gutterBottom>
        Client errors
      </Typography>
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <ClientErrorsTable
          errors={errors}
          hasMore={!!nextCursor}
          loadingMore={loadingMore}
          onLoadMore={() => nextCursor && void load(nextCursor)}
        />
      )}
    </Box>
  );
}

export default ErrorsSection;
